<template>
  <div
    ref="swiperEl"
    :class="[bem.b(), 'swiper']"
    :style="{ height: addUnit(height) }"
  >
    <div :class="[bem.e('wrapper'), 'swiper-wrapper']">
      <div
        v-for="(item, index) in list"
        :key="`slide-${index}`"
        :class="[bem.e('slide'), 'swiper-slide']"
      >
        <slot name="slide" :item="item" :index="index">
          <component
            :is="item.link ? 'a' : 'div'"
            class="slide-content"
            v-bind="item.link ? { href: item.link, target: '_blank' } : {}"
          >
            <img
              v-if="item.src"
              :src="item.src"
              :alt="item.title || `Slide ${index + 1}`"
              class="slide-image"
            />

            <div
              v-if="item.text"
              class="slide-text"
              :style="textStyle"
              v-html="DOMPurify.sanitize(item.text || '', purifyOptions)"
            ></div>
          </component>
        </slot>
      </div>
    </div>

    <!-- Swiper 默认导航元素 -->
    <div v-if="pagination" class="swiper-pagination"></div>
    <div v-if="navigation" class="swiper-button-prev"></div>
    <div v-if="navigation" class="swiper-button-next"></div>
    <div v-if="scrollbar" class="swiper-scrollbar"></div>
  </div>
</template>

<script setup lang="ts">
import { addUnit, createNamespace } from '@ll-plus/utils'
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'

import DOMPurify from 'dompurify'

// 导入所有 Swiper CSS（使用默认样式）
import 'swiper/css'
import 'swiper/css/effect-cards'
import 'swiper/css/effect-coverflow'
import 'swiper/css/effect-cube'
import 'swiper/css/effect-fade'
import 'swiper/css/effect-flip'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import 'swiper/css/scrollbar'

// 静态导入所有模块
import Swiper from 'swiper'
import {
  Autoplay,
  EffectCards,
  EffectCoverflow,
  EffectCube,
  EffectFade,
  EffectFlip,
  Keyboard,
  Mousewheel,
  Navigation,
  Pagination,
  Scrollbar,
} from 'swiper/modules'

import { slideshowProps } from './slideshow'

defineOptions({ name: 'LlSlideshow' })

const bem = createNamespace('slideshow')
const props = defineProps(slideshowProps)
const emits = defineEmits(['init', 'slideChange', 'destroy', 'clickSlide'])

const swiperEl = ref<HTMLElement>()
const swiperInstance = ref()

// 自定义配置允许 SVG
const purifyOptions = computed(() => ({
  USE_PROFILES: { html: true, svg: true },
  ADD_TAGS: ['use', 'svg', 'symbol'],
  ADD_ATTR: ['xlink:href', 'viewBox', 'aria-hidden'],
  FORBID_TAGS: ['script', 'iframe', 'object', 'embed'],
  FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover'],
}))

// 所有模块数组
const allModules = [
  Navigation,
  Pagination,
  Scrollbar,
  Autoplay,
  EffectFade,
  EffectCube,
  EffectCoverflow,
  EffectFlip,
  EffectCards,
  Keyboard, // 键盘控制模块
  Mousewheel, // 鼠标滚轮模块
]

// 构建效果配置
const buildEffectConfig = () => {
  switch (props.effect) {
    case 'fade':
      return {
        effect: props.effect,
      }
    case 'cube':
      return {
        effect: props.effect,
        cubeEffect: {
          shadow: props.shadow,
        },
      }
    case 'coverflow':
      return {
        effect: props.effect,
        coverflowEffect: {
          rotate: props.rotate, //倾斜视角
          stretch: props.stretch, // 拉伸
          depth: props.depth, // 深度
          modifier: 1,
        },
      }
    case 'flip':
      return {
        effect: props.effect,
      }
    case 'cards':
      return {
        effect: props.effect,
        cardsEffect: {
          perSlideRotate: props.perSlideRotate, // 每张卡片旋转角度
          perSlideOffset: props.perSlideOffset, // 每张卡片偏移量
        },
      }
    default:
      return { effect: 'slide' }
  }
}

// 构建 Swiper 配置
const buildSwiperConfig = () => {
  const effectConfig = buildEffectConfig()

  // 基础配置
  const baseConfig = {
    modules: allModules,
    speed: props.speed,
    loop: props.loop,
    direction: props.direction, // 控制水平/垂直方向
    grabCursor: props.grabCursor, // 是否在触摸时显示抓取光标
    simulateTouch: props.simulateTouch, // 是否触摸滑动切换
    slidesPerView: props.slidesPerView, // 同时显示的幻灯片数量
    spaceBetween: props.spaceBetween, // 幻灯片之间的间距
    centeredSlides: props.centeredSlides, // 是否居中显示活动幻灯片
    slidesPerGroup: props.slidesPerGroup, // 每次滑动切换的幻灯片组数

    ...effectConfig,
  }

  // 鼠标滚轮配置
  const mousewheelConfig = props.mousewheel
    ? {
        mousewheel: {
          enabled: props.mousewheel, // 启用鼠标滚轮控制
          invert: props.mousewheelInvert, // 鼠标滚轮方向是否反转
          sensitivity: props.mousewheelSensitivity, // 鼠标滚轮灵敏度 (0.1 - 3)
        },
      }
    : {}

  // 键盘配置
  const keyboardConfig = props.keyboard
    ? {
        keyboard: {
          enabled: props.keyboard, // 是否启用键盘控制
          onlyInViewport: props.keyboardOnlyInViewport, // 是否仅在视窗内响应键盘
          pageUpDown: props.keyboardPageUpDown, // 是否启用PageUp/PageDown翻页
        },
      }
    : {}

  // 自动播放配置
  const autoplayConfig = props.autoplay
    ? {
        autoplay: {
          delay: props.delay,
          disableOnInteraction: false, // 注意：这里建议设为 true 更符合用户习惯
          ...(props.options?.autoplay || {}),
        },
      }
    : {}

  // 分页器配置
  const paginationConfig = props.pagination
    ? {
        pagination: {
          el: '.swiper-pagination',
          clickable: true,
          ...(props.options?.pagination || {}),
        },
      }
    : {}

  // 导航配置
  const navigationConfig = props.navigation
    ? {
        navigation: {
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev',
          ...(props.options?.navigation || {}),
        },
      }
    : {}

  // 滚动条配置
  const scrollbarConfig = props.scrollbar
    ? {
        scrollbar: {
          el: '.swiper-scrollbar',
          draggable: true,
          ...(props.options?.scrollbar || {}),
        },
      }
    : {}

  // 合并配置
  return {
    ...baseConfig,
    ...mousewheelConfig,
    ...keyboardConfig,
    ...autoplayConfig,
    ...paginationConfig,
    ...navigationConfig,
    ...scrollbarConfig,
    ...props.options, // 用户自定义选项最后合并（最高优先级）
  }
}

// 初始化 Swiper
const initSwiper = async () => {
  await nextTick()

  if (!swiperEl.value || props.list.length === 0) return

  try {
    if (swiperInstance.value) {
      swiperInstance.value.destroy(true, true)
    }

    const config = buildSwiperConfig()

    swiperInstance.value = new Swiper(swiperEl.value, {
      ...config,
      on: {
        init: () => {
          emits('init', swiperInstance.value!)
        },
        slideChange: () => {
          emits('slideChange', swiperInstance.value!)
        },
        destroy: () => {
          emits('destroy')
        },
        click: (swiper, event) => {
          emits('clickSlide', {
            swiper,
            event,
            activeIndex: swiper.activeIndex,
          })
        },
      },
    })

    // 确保幻灯片正确显示
    nextTick(() => {
      if (swiperInstance.value) {
        swiperInstance.value.update()
      }
    })
  } catch (error) {
    console.error(error)
  }
}

// 重新初始化（效果切换时使用）
const reInitSwiper = async () => {
  if (swiperInstance.value) {
    swiperInstance.value.destroy(true, true)
    swiperInstance.value = null
  }

  await nextTick()
  await initSwiper()
}

// 监听效果变化
watch(
  () => props.effect,
  (newEffect, oldEffect) => {
    if (newEffect !== oldEffect) {
      reInitSwiper()
    }
  },
)

// 监听列表变化
watch(
  () => props.list,
  (newList, oldList) => {
    if (newList.length !== oldList.length && swiperInstance.value) {
      nextTick(() => {
        swiperInstance.value?.update()
      })
    }
  },
  { deep: true },
)

// 监听配置变化
watch(
  () => props.options,
  () => {
    if (swiperInstance.value) {
      swiperInstance.value.update()
    }
  },
  { deep: true },
)

// 组件生命周期
onMounted(() => {
  initSwiper()
})

onUnmounted(() => {
  if (swiperInstance.value) {
    swiperInstance.value.destroy(true, true)
  }
})

// 暴露方法给父组件
defineExpose({
  instance: swiperInstance,
  next: () => swiperInstance.value?.slideNext(),
  prev: () => swiperInstance.value?.slidePrev(),
  goTo: (index: number) => swiperInstance.value?.slideTo(index),
  startAutoplay: () => swiperInstance.value?.autoplay?.start(),
  stopAutoplay: () => swiperInstance.value?.autoplay?.stop(),
  update: () => swiperInstance.value?.update(),
  destroy: () => {
    if (swiperInstance.value) {
      swiperInstance.value.destroy(true, true)
      swiperInstance.value = null
    }
  },
  reInit: reInitSwiper,
})
</script>
